import React, { useState, useMemo, useEffect } from 'react';
import {
  Paper, Typography, Button, TextField, Stack, Divider, Box, IconButton, 
  Grid, Card, Chip, InputAdornment, Badge, Avatar, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsIcon from '@mui/icons-material/Payments';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import DeleteIcon from '@mui/icons-material/Delete';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import InvoiceDialog from './InvoiceDialog';

const Cashier = ({ products, onSale, loading, user, currentSession, sale, editMode }) => {
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem('cashierCart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [customer, setCustomer] = useState(null);
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cartItemSize, setCartItemSize] = useState('compact');
  const [customers, setCustomers] = useState([]);
  
  // Payment dialog states
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paidAmount, setPaidAmount] = useState('');
  const [cardReference, setCardReference] = useState('');
  const [change, setChange] = useState(0);
  
  // Customer pending bills
  const [customerPending, setCustomerPending] = useState({ total: 0, count: 0 });
  
  // Invoice dialog states
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [completedSale, setCompletedSale] = useState(null);
  const [invoiceCustomerPending, setInvoiceCustomerPending] = useState(null);
  
  // Refs for payment inputs
  const paidAmountInputRef = React.useRef(null);
  const cardRefInputRef = React.useRef(null);

  // Load customers
  useEffect(() => {
    if (!user) return;
    const fetchCustomers = async () => {
      try {
        const res = await fetch('/api/customers', {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCustomers(data);
        }
      } catch (error) {
        console.error('Failed to fetch customers:', error);
      }
    };
    fetchCustomers();
  }, [user]);
  
  // Load sale data when editing unpaid bill
  useEffect(() => {
    if (editMode && sale) {
      // Load cart items from sale
      if (sale.products && sale.products.length > 0) {
        const cartItems = sale.products.map(item => ({
          product: item.product._id || item.product,
          name: item.product.name || item.name || 'Unknown Product',
          brand: item.product.brand || item.brand || '',
          price: item.price || 0,
          quantity: item.quantity || 1,
          stock: item.product.stock || 0
        }));
        setCart(cartItems);
      }
      
      // Load customer
      if (sale.customer) {
        if (typeof sale.customer === 'object') {
          setCustomer(sale.customer);
        } else {
          // If customer is just an ID, find it in customers list
          const foundCustomer = customers.find(c => c._id === sale.customer);
          if (foundCustomer) setCustomer(foundCustomer);
        }
      }
      
      // Load notes
      if (sale.notes) {
        setNotes(sale.notes);
      }
      
      // Load payment method
      if (sale.paymentMethod) {
        setPaymentMethod(sale.paymentMethod);
      }
    }
  }, [editMode, sale, customers]);
  
  // Auto-focus payment input when dialog opens
  useEffect(() => {
    if (paymentDialogOpen) {
      setTimeout(() => {
        if (paymentMethod === 'cash' && paidAmountInputRef.current) {
          paidAmountInputRef.current.focus();
          paidAmountInputRef.current.select();
        } else if (paymentMethod === 'card' && cardRefInputRef.current) {
          cardRefInputRef.current.focus();
          cardRefInputRef.current.select();
        }
      }, 100);
    }
  }, [paymentDialogOpen, paymentMethod]);
  
  // Fetch customer pending bills when customer is selected
  useEffect(() => {
    const fetchCustomerPending = async () => {
      if (!customer?._id || !user) {
        setCustomerPending({ total: 0, count: 0 });
        return;
      }
      
      try {
        // If editing a sale, exclude it from pending bills calculation
        const excludeSaleId = editMode && sale?._id ? `?excludeSaleId=${sale._id}` : '';
        const res = await fetch(`/api/customers/${customer._id}/pending${excludeSaleId}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setCustomerPending({
            total: data.totalPending || 0,
            count: data.count || 0
          });
        }
      } catch (error) {
        console.error('Failed to fetch customer pending bills:', error);
        setCustomerPending({ total: 0, count: 0 });
      }
    };
    
    fetchCustomerPending();
  }, [customer, user, editMode, sale]);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cashierCart', JSON.stringify(cart));
  }, [cart]);

  // Load cart item size preference
  useEffect(() => {
    const fetchDisplaySettings = async () => {
      try {
        const res = await fetch('/api/settings/cashier-display');
        if (res.ok) {
          const data = await res.json();
          if (data.cartItemSize) {
            setCartItemSize(data.cartItemSize);
          }
        }
      } catch (err) {
        console.error('Failed to fetch display settings:', err);
      }
    };
    fetchDisplaySettings();

    // Listen for settings changes
    const handleSettingsChange = (e) => {
      if (e.detail?.cartItemSize) {
        setCartItemSize(e.detail.cartItemSize);
      }
    };
    window.addEventListener('cashierDisplaySettingsChanged', handleSettingsChange);
    return () => window.removeEventListener('cashierDisplaySettingsChanged', handleSettingsChange);
  }, []);

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(p => 
      p.name.toLowerCase().includes(term) || 
      p.brand.toLowerCase().includes(term) ||
      p.sku?.toLowerCase().includes(term)
    );
  }, [products, searchTerm]);

  const addToCart = (prod) => {
    const exist = cart.find(item => item.product === prod._id);
    if (exist) {
      setCart(cart.map(item => 
        item.product === prod._id 
          ? { ...item, quantity: Math.min(item.quantity + 1, prod.stock) } 
          : item
      ));
    } else {
      setCart([...cart, { 
        product: prod._id, 
        name: prod.name, 
        brand: prod.brand,
        price: prod.price, 
        quantity: 1, 
        stock: prod.stock 
      }]);
    }
  };

  const removeFromCart = (id) => {
    setCart(cart.filter(item => item.product !== id));
  };

  const changeQty = (id, delta) => {
    setCart(cart.map(item =>
      item.product === id ? { ...item, quantity: Math.max(1, item.quantity + delta) } : item
    ));
  };

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // Cart item size configurations
  const sizeConfig = {
    compact: {
      padding: { px: 1, py: 0.5 },
      containerPadding: 0.5,
      fontSize: { name: '0.8rem', details: '0.65rem', price: '0.85rem', qty: '0.75rem' },
      lineHeight: { name: 1.3, details: 1.2 },
      iconSize: { action: 14, delete: 16 },
      buttonSize: { width: 24, height: 24, padding: 0.3 },
      spacing: 0.5
    },
    comfortable: {
      padding: { px: 1.5, py: 0.75 },
      containerPadding: 1,
      fontSize: { name: '0.875rem', details: '0.75rem', price: '0.95rem', qty: '0.85rem' },
      lineHeight: { name: 1.4, details: 1.3 },
      iconSize: { action: 16, delete: 18 },
      buttonSize: { width: 28, height: 28, padding: 0.5 },
      spacing: 0.75
    },
    spacious: {
      padding: { px: 2, py: 1 },
      containerPadding: 1.5,
      fontSize: { name: '0.95rem', details: '0.8rem', price: '1rem', qty: '0.95rem' },
      lineHeight: { name: 1.5, details: 1.4 },
      iconSize: { action: 18, delete: 20 },
      buttonSize: { width: 32, height: 32, padding: 0.6 },
      spacing: 1
    }
  };

  const config = sizeConfig[cartItemSize] || sizeConfig.compact;

  const handleOpenPayment = () => {
    if (cart.length === 0) return;
    
    // Check if there's an active cashier session (only for new sales, not when editing)
    if (!editMode && !currentSession) {
      alert('Please open a cashier session before making a sale');
      return;
    }
    
    setPaidAmount('');
    setCardReference('');
    setChange(0);
    setPaymentDialogOpen(true);
  };
  
  const handleConfirmPayment = async () => {
    const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const paid = parseFloat(paidAmount) || 0;
    
    if (paymentMethod === 'cash' && paid < total) {
      alert('Paid amount must be greater than or equal to total');
      return;
    }
    
    if (paymentMethod === 'card' && !cardReference.trim()) {
      alert('Please enter card reference number');
      return;
    }
    
    const calculatedChange = paymentMethod === 'cash' ? paid - total : 0;
    
    // Capture current customer pending before clearing
    let pendingForInvoice = null;
    if (customer?._id) {
      pendingForInvoice = { ...customerPending };
    }
    
    const savedSale = await onSale({
      products: cart.map(item => ({ product: item.product, quantity: item.quantity })),
      customer: customer?._id || undefined,
      notes: notes.trim(),
      paymentMethod,
      paidAmount: paid,
      change: calculatedChange,
      paymentStatus: 'Paid',
      cardReference: paymentMethod === 'card' ? cardReference : undefined,
    });
    
    // Show invoice dialog if sale was successful
    if (savedSale) {
      setCompletedSale(savedSale);
      setInvoiceCustomerPending(pendingForInvoice);
      setInvoiceDialogOpen(true);
    }
    
    setCart([]);
    localStorage.removeItem('cashierCart');
    setCustomer(null);
    setNotes('');
    setSearchTerm('');
    setPaymentDialogOpen(false);
    setPaidAmount('');
    setCardReference('');
    setChange(0);
  };
  
  // Hold Order (Save as unpaid bill)
  const handleHoldOrder = async () => {
    if (cart.length === 0) return;
    
    // Check if there's an active cashier session (only for new sales, not when editing)
    if (!editMode && !currentSession) {
      alert('Please open a cashier session before holding an order');
      return;
    }
    
    const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    await onSale({
      products: cart.map(item => ({ product: item.product, quantity: item.quantity })),
      customer: customer?._id || undefined,
      notes: notes.trim(),
      paymentMethod,
      paidAmount: 0,
      change: 0,
      paymentStatus: 'Pending',
    });
    
    setCart([]);
    localStorage.removeItem('cashierCart');
    setCustomer(null);
    setNotes('');
    setSearchTerm('');
  };

  const clearCart = () => {
    setCart([]);
    localStorage.removeItem('cashierCart');
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', bgcolor: '#f0f2f5', display: 'flex', overflow: 'hidden' }}>
      {/* Left Side - Products Grid */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Search Bar */}
        <Box sx={{ p: 2, bgcolor: 'white', borderBottom: '2px solid #e0e0e0' }}>
          <TextField
            fullWidth
            placeholder="Search products by name, brand, or SKU..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#666' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                bgcolor: '#f8f9fa',
                '&:hover': { bgcolor: '#fff' }
              } 
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" color="text.secondary">
              {filteredProducts.length} products available
            </Typography>
            {searchTerm && (
              <Chip 
                label={`Searching: "${searchTerm}"`} 
                size="small" 
                onDelete={() => setSearchTerm('')}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        {/* Products Grid */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
          <Grid container spacing={2}>
            {filteredProducts.map(product => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={product._id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': { 
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    },
                    '&:active': {
                      transform: 'translateY(0)',
                    },
                    border: '1px solid #e0e0e0',
                    position: 'relative'
                  }}
                  onClick={() => product.stock > 0 && addToCart(product)}
                >
                  {/* Stock Badge */}
                  {product.stock < 10 && product.stock > 0 && (
                    <Chip 
                      label={`${product.stock} left`}
                      size="small"
                      color="warning"
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        right: 8, 
                        fontSize: '0.7rem',
                        height: 20
                      }}
                    />
                  )}
                  {product.stock === 0 && (
                    <Chip 
                      label="Out of Stock"
                      size="small"
                      color="error"
                      sx={{ 
                        position: 'absolute', 
                        top: 8, 
                        right: 8, 
                        fontSize: '0.7rem',
                        height: 20
                      }}
                    />
                  )}

                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    {/* Product Icon */}
                    <Avatar 
                      sx={{ 
                        width: 60, 
                        height: 60, 
                        bgcolor: product.stock === 0 ? '#ccc' : '#e3f2fd',
                        color: product.stock === 0 ? '#666' : '#1976d2',
                        mx: 'auto',
                        mb: 1.5
                      }}
                    >
                      <InventoryIcon sx={{ fontSize: 32 }} />
                    </Avatar>

                    {/* Product Name */}
                    <Typography 
                      variant="body2" 
                      fontWeight={600} 
                      sx={{ 
                        mb: 0.5,
                        height: 40,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        color: product.stock === 0 ? '#999' : 'inherit'
                      }}
                    >
                      {product.name}
                    </Typography>

                    {/* Brand */}
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ display: 'block', mb: 1 }}
                    >
                      {product.brand}
                    </Typography>

                    {/* Price */}
                    <Typography 
                      variant="h6" 
                      fontWeight={700}
                      color={product.stock === 0 ? 'text.disabled' : 'primary'}
                    >
                      ${product.price.toFixed(2)}
                    </Typography>

                    {/* Add Button */}
                    {product.stock > 0 && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        fullWidth
                        sx={{ 
                          mt: 1,
                          bgcolor: '#4caf50',
                          '&:hover': { bgcolor: '#388e3c' }
                        }}
                      >
                        Add
                      </Button>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          {filteredProducts.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <SearchIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No products found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Right Side - Cart & Checkout */}
      <Paper 
        elevation={3}
        sx={{ 
          width: { xs: '100%', md: 500 }, 
          display: 'flex', 
          flexDirection: 'column',
          borderRadius: 0,
          borderLeft: '2px solid #e0e0e0'
        }}
      >
        {/* Cart Header */}
        <Box sx={{ 
          p: 2, 
          bgcolor: '#1976d2', 
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={cart.reduce((sum, item) => sum + item.quantity, 0)} color="error">
              <ShoppingCartIcon />
            </Badge>
            <Typography variant="h6" fontWeight={700}>
              Current Order
            </Typography>
          </Box>
          {cart.length > 0 && (
            <IconButton size="small" onClick={clearCart} sx={{ color: 'white' }}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>

        {/* Cart Items */}
        <Box sx={{ flex: 1, overflow: 'auto', bgcolor: '#fafafa' }}>
          {cart.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, px: 2 }}>
              <ShoppingCartIcon sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" fontWeight={500}>
                Cart is empty
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Click on products to add them to cart
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: config.containerPadding }}>
              {cart.map(item => (
                <Box
                  key={item.product}
                  sx={{
                    ...config.padding,
                    mb: config.spacing,
                    bgcolor: 'white',
                    borderRadius: 0.5,
                    border: '1px solid #e8e8e8',
                    '&:hover': { 
                      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                      borderColor: '#d0d0d0'
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={600} 
                      sx={{ 
                        fontSize: config.fontSize.name,
                        lineHeight: config.lineHeight.name,
                        wordBreak: 'break-word',
                        flex: 1,
                        mr: 1
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          fontSize: config.fontSize.details,
                          whiteSpace: 'nowrap',
                          minWidth: 60,
                          textAlign: 'right'
                        }}
                      >
                        ${item.price.toFixed(2)}
                      </Typography>
                      <IconButton 
                        size="small"
                        onClick={() => changeQty(item.product, -1)}
                        disabled={item.quantity <= 1}
                        sx={{ 
                          bgcolor: '#f5f5f5',
                          p: config.buttonSize.padding,
                          minWidth: config.buttonSize.width,
                          width: config.buttonSize.width,
                          height: config.buttonSize.height,
                          '&:hover': { bgcolor: '#e0e0e0' },
                          '&:disabled': { bgcolor: '#fafafa' }
                        }}
                      >
                        <RemoveIcon sx={{ fontSize: config.iconSize.action }} />
                      </IconButton>
                      <Typography 
                        sx={{ 
                          minWidth: config.buttonSize.width,
                          textAlign: 'center',
                          fontWeight: 700,
                          fontSize: config.fontSize.qty
                        }}
                      >
                        {item.quantity}
                      </Typography>
                      <IconButton 
                        size="small"
                        onClick={() => changeQty(item.product, 1)}
                        disabled={item.quantity >= item.stock}
                        sx={{ 
                          bgcolor: '#f5f5f5',
                          p: config.buttonSize.padding,
                          minWidth: config.buttonSize.width,
                          width: config.buttonSize.width,
                          height: config.buttonSize.height,
                          '&:hover': { bgcolor: '#e0e0e0' },
                          '&:disabled': { bgcolor: '#fafafa' }
                        }}
                      >
                        <AddIcon sx={{ fontSize: config.iconSize.action }} />
                      </IconButton>
                      <Typography 
                        variant="body2" 
                        fontWeight={700} 
                        color="primary" 
                        sx={{ 
                          fontSize: config.fontSize.price, 
                          minWidth: 50, 
                          textAlign: 'right' 
                        }}
                      >
                        ${(item.price * item.quantity).toFixed(2)}
                      </Typography>
                      <IconButton 
                        size="small"
                        onClick={() => removeFromCart(item.product)}
                        sx={{ 
                          color: '#d32f2f',
                          p: config.buttonSize.padding,
                          minWidth: config.buttonSize.width,
                          width: config.buttonSize.width,
                          height: config.buttonSize.height
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: config.iconSize.delete }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* Customer & Payment */}
        <Box sx={{ p: 2, bgcolor: 'white', borderTop: '2px solid #e0e0e0' }}>
          <Autocomplete
            options={customers}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return option?.name || '';
            }}
            value={customer}
            onChange={(event, newValue) => {
              setCustomer(newValue);
            }}
            isOptionEqualToValue={(option, value) => option._id === value?._id}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                size="small"
                placeholder="Customer name (optional)"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}
            sx={{ mb: 1.5 }}
          />
          
          {/* Customer Pending Bills */}
          {customer && customerPending.count > 0 && (
            <Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ff9800' }}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: '#ff9800' 
                  }} />
                  <Typography variant="caption" fontWeight={600} color="#e65100">
                    Pending Bills: {customerPending.count}
                  </Typography>
                </Stack>
                <Typography variant="body2" fontWeight={700} color="#e65100">
                  ${customerPending.total.toFixed(2)}
                </Typography>
              </Stack>
            </Box>
          )}

          <TextField
            fullWidth
            size="small"
            placeholder="Add notes (optional)"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            rows={2}
            sx={{ mb: 2 }}
          />

          {/* Payment Method */}
          <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            PAYMENT METHOD
          </Typography>
          <Grid container spacing={1} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant={paymentMethod === 'cash' ? 'contained' : 'outlined'}
                onClick={() => setPaymentMethod('cash')}
                startIcon={<PaymentsIcon />}
                sx={{ 
                  py: 1.5,
                  bgcolor: paymentMethod === 'cash' ? '#4caf50' : 'transparent',
                  borderColor: '#4caf50',
                  color: paymentMethod === 'cash' ? 'white' : '#4caf50',
                  '&:hover': {
                    bgcolor: paymentMethod === 'cash' ? '#388e3c' : 'rgba(76, 175, 80, 0.1)',
                    borderColor: '#4caf50'
                  }
                }}
              >
                Cash
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant={paymentMethod === 'card' ? 'contained' : 'outlined'}
                onClick={() => setPaymentMethod('card')}
                startIcon={<CreditCardIcon />}
                sx={{ 
                  py: 1.5,
                  bgcolor: paymentMethod === 'card' ? '#2196f3' : 'transparent',
                  borderColor: '#2196f3',
                  color: paymentMethod === 'card' ? 'white' : '#2196f3',
                  '&:hover': {
                    bgcolor: paymentMethod === 'card' ? '#1976d2' : 'rgba(33, 150, 243, 0.1)',
                    borderColor: '#2196f3'
                  }
                }}
              >
                Card
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Total & Checkout */}
        <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderTop: '2px solid #e0e0e0' }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            p: 2,
            bgcolor: 'white',
            borderRadius: 1
          }}>
            <Typography variant="h6" fontWeight={700}>
              TOTAL
            </Typography>
            <Typography variant="h4" fontWeight={700} color="primary">
              ${total.toFixed(2)}
            </Typography>
          </Box>

          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              size="large"
              onClick={handleHoldOrder}
              disabled={cart.length === 0 || loading || (!editMode && !currentSession)}
              startIcon={<HourglassEmptyIcon />}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                borderColor: '#ff9800',
                color: '#ff9800',
                '&:hover': { 
                  borderColor: '#f57c00',
                  bgcolor: 'rgba(255, 152, 0, 0.08)'
                },
                '&:disabled': { 
                  borderColor: '#e0e0e0',
                  color: '#9e9e9e'
                }
              }}
            >
              HOLD ORDER
            </Button>
            
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleOpenPayment}
              disabled={cart.length === 0 || loading || (!editMode && !currentSession)}
              sx={{
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                bgcolor: '#1976d2',
                '&:hover': { bgcolor: '#1565c0' },
                '&:disabled': { bgcolor: '#e0e0e0' }
              }}
            >
              {loading ? 'PROCESSING...' : editMode ? 'COMPLETE ORDER' : 'COMPLETE SALE'}
            </Button>
          </Stack>
        </Box>
      </Paper>
      
      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onClose={() => setPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Complete Payment - {paymentMethod === 'cash' ? 'Cash' : 'Card'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">Total Amount:</Typography>
                <Typography variant="h5" fontWeight={700} color="primary">
                  ${cart.reduce((sum, item) => sum + (item.price * item.quantity), 0).toFixed(2)}
                </Typography>
              </Stack>
            </Box>
            
            {paymentMethod === 'cash' ? (
              <>
                <TextField
                  label="Amount Paid"
                  type="number"
                  value={paidAmount}
                  onChange={(e) => {
                    const paid = parseFloat(e.target.value) || 0;
                    const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
                    setPaidAmount(e.target.value);
                    setChange(paid - total);
                  }}
                  inputProps={{ min: 0, step: 0.01 }}
                  inputRef={paidAmountInputRef}
                  fullWidth
                  required
                  onFocus={(e) => e.target.select()}
                  helperText="Enter the amount received from customer"
                />
                
                {change >= 0 && paidAmount && (
                  <Box sx={{ p: 2, bgcolor: change === 0 ? '#e8f5e9' : '#fff3e0', borderRadius: 1, border: '2px solid', borderColor: change === 0 ? '#4caf50' : '#ff9800' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">Change:</Typography>
                      <Typography variant="h4" fontWeight={700} color={change === 0 ? '#4caf50' : '#ff9800'}>
                        ${change.toFixed(2)}
                      </Typography>
                    </Stack>
                  </Box>
                )}
              </>
            ) : (
              <TextField
                label="Card Reference Number"
                value={cardReference}
                onChange={(e) => setCardReference(e.target.value)}
                inputRef={cardRefInputRef}
                fullWidth
                required
                onFocus={(e) => e.target.select()}
                helperText="Enter the card transaction reference/approval code"
                placeholder="e.g., AUTH123456"
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmPayment}
            disabled={loading}
            sx={{ minWidth: 120 }}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Invoice Dialog */}
      <InvoiceDialog
        open={invoiceDialogOpen}
        sale={completedSale}
        onClose={() => {
          setInvoiceDialogOpen(false);
          setCompletedSale(null);
          setInvoiceCustomerPending(null);
        }}
        customerPending={invoiceCustomerPending}
      />
    </Box>
  );
};

export default Cashier;
